import { flowNodes, scopeStatuses, kpis, ownerStyles } from '../data'

export function WrittenView() {
  return (
    <div className="max-w-3xl mx-auto space-y-8">
      {/* Title block */}
      <div className="p-6 rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-color)]">
        <h2 className="text-lg font-bold text-[var(--text-primary)]">
          Presales & Implementations Process
        </h2>
        <p className="text-sm text-[var(--text-muted)] mt-2 leading-relaxed">
          This document describes the end-to-end process from deal trigger through to implementation go-live.
          It covers the presales engagement model, scope lifecycle, handover to implementations, and the
          KPIs used to measure presales performance.
        </p>
      </div>

      {/* Process Steps */}
      <section className="space-y-4">
        <h3 className="text-sm font-bold text-[var(--text-primary)] uppercase tracking-wider">
          Process Steps
        </h3>

        {flowNodes.map((node, i) => {
          const style = ownerStyles[node.owner]
          return (
            <div
              key={node.id}
              className="p-4 rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)]"
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-mono text-[var(--text-muted)]">{i + 1}.</span>
                <h4 className="text-sm font-semibold text-[var(--text-primary)]">{node.title}</h4>
                <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${style.bg} ${style.text}`}>
                  {style.label}
                </span>
              </div>

              {node.subtitle && (
                <p className="text-xs text-[var(--text-secondary)] mb-2 italic">{node.subtitle}</p>
              )}

              {node.metric && (
                <div className="flex items-start gap-2 mb-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-amber-400 shrink-0 mt-0.5">Metric:</span>
                  <span className="text-xs text-[var(--text-secondary)]">{node.metric}</span>
                </div>
              )}

              {/* What happens */}
              <div className="mb-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)] mb-1">
                  What happens
                </p>
                <ul className="space-y-1 ml-5">
                  {node.whatWeDo.map((item, j) => (
                    <li key={j} className="text-xs text-[var(--text-muted)] list-disc">{item}</li>
                  ))}
                </ul>
              </div>

              {/* How it works */}
              {node.howItWorks && (
                <div className="mb-2">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)] mb-1">
                    How it works
                  </p>
                  <ul className="space-y-1 ml-5">
                    {node.howItWorks.map((item, j) => (
                      <li key={j} className="text-xs text-[var(--text-muted)] list-disc">{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Branch */}
              {node.branch && (
                <div className="mt-3 ml-5 p-3 rounded-lg border border-dashed border-[var(--border-color)] bg-[var(--bg-tertiary)]">
                  <p className="text-xs font-medium text-[var(--text-secondary)] mb-1">
                    Parallel: {node.branch.title}
                  </p>
                  <ul className="space-y-1 ml-3">
                    {node.branch.details.map((d, k) => (
                      <li key={k} className="text-xs text-[var(--text-muted)] list-disc">{d}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Sub-processes */}
              {node.subProcesses && node.subProcesses.length > 0 && (
                <div className="mt-3 space-y-3">
                  {node.subProcesses.map((sub) => (
                    <div key={sub.title} className="ml-5 p-3 rounded-lg border border-[var(--border-color)] bg-[var(--bg-tertiary)]">
                      <p className="text-xs font-semibold text-[var(--text-primary)] mb-1">{sub.title}</p>
                      {sub.metric && (
                        <div className="flex items-start gap-1.5 mb-1.5 text-amber-400">
                          <span className="text-[9px] font-bold uppercase tracking-wider">Metric:</span>
                          <span className="text-[10px] text-[var(--text-secondary)]">{sub.metric}</span>
                        </div>
                      )}
                      <ul className="space-y-1 ml-3">
                        {sub.details.map((d, k) => (
                          <li key={k} className="text-xs text-[var(--text-muted)] list-disc">{d}</li>
                        ))}
                      </ul>
                      {sub.whyItMatters && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          <span className="text-[9px] font-bold uppercase tracking-wider text-[var(--text-muted)]">Why:</span>
                          {sub.whyItMatters.map((r, k) => (
                            <span key={k} className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--accent-color)]/10 text-[var(--accent-color)]">
                              {r}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </section>

      {/* Scope Lifecycle */}
      <section className="space-y-4">
        <h3 className="text-sm font-bold text-[var(--text-primary)] uppercase tracking-wider">
          Scope Status Lifecycle
        </h3>
        <div className="p-4 rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)]">
          <p className="text-xs text-[var(--text-muted)] mb-3">
            Each scope in DealHub progresses through the following statuses:
          </p>
          <div className="space-y-3">
            {scopeStatuses.map((status) => (
              <div key={status.id} className="flex gap-3">
                <span className={`text-xs font-semibold ${status.color} w-20 shrink-0`}>
                  {status.label}
                </span>
                <span className="text-xs text-[var(--text-muted)]">{status.description}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-[var(--text-muted)] mt-3 italic">
            The Sent/Adjust loop can repeat multiple times until the customer is satisfied.
            DealHub enforces 100% field completion before a scope can move to Confirmed.
          </p>
        </div>
      </section>

      {/* KPIs */}
      <section className="space-y-4">
        <h3 className="text-sm font-bold text-[var(--text-primary)] uppercase tracking-wider">
          Presales KPIs (Key Results)
        </h3>
        {kpis.map((kpi) => (
          <div
            key={kpi.id}
            className="p-4 rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)]"
          >
            <h4 className="text-sm font-semibold text-[var(--text-primary)]">{kpi.label}</h4>
            <p className="text-xs text-[var(--text-secondary)] mt-1">{kpi.description}</p>
            <div className="mt-2 flex gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--accent-color)]">
                Target:
              </span>
              <span className="text-xs text-[var(--text-muted)]">{kpi.target}</span>
            </div>
            <div className="mt-2 flex gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                Metric:
              </span>
              <span className="text-xs text-[var(--text-muted)]">{kpi.metric}</span>
            </div>
            <ul className="mt-2 ml-4 space-y-1">
              {kpi.how.map((h, i) => (
                <li key={i} className="text-xs text-[var(--text-muted)] list-disc">{h}</li>
              ))}
            </ul>
          </div>
        ))}
      </section>
    </div>
  )
}
